import type {
  SandboxConversationDetail,
  SandboxConversationSummary,
  SandboxPendingApproval,
} from "@/types/sandbox";

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    const message = (data as { error?: string }).error || "Request failed";
    throw new Error(message);
  }
  return data as T;
}

export async function listSandboxConversations(): Promise<SandboxConversationSummary[]> {
  const response = await fetch("/api/v1/sandbox/conversations");
  return parseJson<SandboxConversationSummary[]>(response);
}

export async function createSandboxConversation(payload: { title: string }): Promise<SandboxConversationSummary> {
  const response = await fetch("/api/v1/sandbox/conversations", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ conversation: payload }),
  });
  return parseJson<SandboxConversationSummary>(response);
}

export async function showSandboxConversation(id: number): Promise<SandboxConversationDetail> {
  const response = await fetch(`/api/v1/sandbox/conversations/${id}`);
  return parseJson<SandboxConversationDetail>(response);
}

export async function updateSandboxConversation(
  id: number,
  payload: Partial<{ title: string }>,
): Promise<SandboxConversationSummary> {
  const response = await fetch(`/api/v1/sandbox/conversations/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ conversation: payload }),
  });
  return parseJson<SandboxConversationSummary>(response);
}

export async function archiveSandboxConversation(id: number): Promise<SandboxConversationSummary> {
  const response = await fetch(`/api/v1/sandbox/conversations/${id}`, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  return parseJson<SandboxConversationSummary>(response);
}

export async function sendSandboxMessage(conversationId: number, content: string) {
  const response = await fetch(`/api/v1/sandbox/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ message: { content } }),
  });
  return parseJson<{ id: number; content: string; role: "user"; created_at: string }>(response);
}

/** Records the decision and resumes the agent. Answers with the calls still waiting. */
export async function decideSandboxToolApproval(conversationId: number, toolCallId: string, approved: boolean) {
  const response = await fetch(`/api/v1/sandbox/conversations/${conversationId}/tool_approvals`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ tool_call_id: toolCallId, approved }),
  });
  return parseJson<{ pending_approvals: SandboxPendingApproval[] }>(response);
}
